package com.schoolconnect.app.feature.parentnotes

import com.schoolconnect.app.datasource.network.response.ParentNoteResponse
import com.schoolconnect.app.datasource.network.response.TeacherClassStatistics
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.ParentNoteCreateRequest
import com.schoolconnect.app.datasource.network.request.ParentNoteUpdateRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.parentnotes.ParentNotesRepository
import com.schoolconnect.app.repository.mystudents.TeacherMyStudentsRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

class ParentNotesViewModel(
    private val schoolId: String = UserSessionStore.getHomeRoleSchoolId(),
    autoLoad: Boolean = true,
) {
    private companion object {
        const val STUDENT_PICKER_PAGE_SIZE = 20
        const val STUDENT_CONTACT_TIMEOUT_MS = 15_000L
    }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var listJob: Job? = null
    private var statisticsJob: Job? = null
    private var pickerJob: Job? = null
    private var studentContactJob: Job? = null
    private var createJob: Job? = null
    private var viewJob: Job? = null
    private var updateJob: Job? = null
    private var conversationJob: Job? = null
    private val _uiState = MutableStateFlow(ParentNotesUiState())
    val uiState: StateFlow<ParentNotesUiState> = _uiState.asStateFlow()

    init {
        if (autoLoad) loadInitialData()
    }

    /** Starts network work only when the parent-notes surface is actually needed. */
    fun loadInitialData() {
        // My Students opens this ViewModel lazily from each row action. Once the
        // picker data is loaded, reopening the dialog must not fan out another
        // page, statistics, and roster request set.
        if (listJob?.isActive == true || statisticsJob?.isActive == true) return
        loadPage()
        loadStatistics()
        loadPickerOptions()
    }

    fun onEvent(event: ParentNotesUiEvent) {
        when (event) {
            ParentNotesUiEvent.RefreshClicked -> {
                loadPage()
                loadStatistics()
            }
            // Dialog pickers need the complete backend-provided option set; table
            // class changes reload the student options with a server-side scope.
            ParentNotesUiEvent.PickerOptionsRequested -> loadPickerOptions(null, reset = true)
            is ParentNotesUiEvent.StudentPickerSearchChanged -> {
                _uiState.update { it.copy(studentPickerQuery = event.query, studentPickerPageIndex = 0) }
                loadPickerOptions(reset = true)
            }
            ParentNotesUiEvent.StudentPickerLoadMore -> {
                val state = _uiState.value
                if (!state.pickerLoading && !state.studentPickerLoadingMore && state.studentPickerHasMore) {
                    loadPickerOptions(pageIndex = state.studentPickerPageIndex + 1, reset = false)
                }
            }
            is ParentNotesUiEvent.StudentPrefilled -> {
                val student = event.student.copy(
                    id = event.student.id.trim(),
                    name = event.student.name.trim(),
                    className = event.student.className.trim(),
                    sectionName = event.student.sectionName.trim(),
                    rollNumber = event.student.rollNumber.trim()
                )
                if (student.id.isNotBlank()) {
                    pickerJob?.cancel()
                    _uiState.update {
                        it.copy(
                            pickerLoading = false,
                            pickerError = "",
                            classPickerOptions = emptyList(),
                            classSectionPickerOptions = emptyList(),
                            sectionPickerOptions = listOf(ParentNoteSectionOption(student.className, student.sectionName)),
                            studentPickerOptions = listOf(student),
                            selectedStudentContact = null
                        )
                    }
                } else {
                    _uiState.update {
                        it.copy(pickerLoading = false, pickerError = "The selected student is missing a valid ID.")
                    }
                }
            }
            is ParentNotesUiEvent.StudentPickerScopeChanged -> {
                _uiState.update { it.copy(studentPickerPageIndex = 0) }
                loadPickerOptions(event.classSectionId, reset = true)
            }
            is ParentNotesUiEvent.SearchChanged -> {
                _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }
                loadPage(debounce = true)
            }
            is ParentNotesUiEvent.FiltersChanged -> {
                val previousClassSectionId = _uiState.value.filters.classSectionId
                _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }
                if (previousClassSectionId != event.filters.classSectionId) {
                    _uiState.update { it.copy(studentPickerPageIndex = 0) }
                    loadPickerOptions(event.filters.classSectionId, reset = true)
                }
                loadPage()
            }
            ParentNotesUiEvent.FiltersReset -> {
                _uiState.update { it.copy(searchQuery = "", filters = ParentNotesFilters(), pageIndex = 0) }
                loadPage()
            }
            is ParentNotesUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                loadPage()
            }
            is ParentNotesUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }
                loadPage()
            }
            is ParentNotesUiEvent.SortChanged -> {
                _uiState.update {
                    val ascending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0)
                }
                loadPage()
            }
            is ParentNotesUiEvent.StudentSelected -> loadStudentContact(event.studentId)
            is ParentNotesUiEvent.CreateRequested -> createNote(event.draft)
            is ParentNotesUiEvent.UpdateRequested -> updateNote(event.draft)
            is ParentNotesUiEvent.ReplyRequested -> createReply(event.parentNoteId, event.message, event.attachmentFileIds)
            is ParentNotesUiEvent.CloseRequested -> closeNote(event.parentNoteId)
            is ParentNotesUiEvent.ViewRequested -> loadView(event.parentNoteId)
            is ParentNotesUiEvent.DeleteRequested -> deleteNote(event.parentNoteId)
            ParentNotesUiEvent.ViewDismissed -> _uiState.update { it.copy(viewedNote = null, viewedReplies = emptyList(), viewedConversationInfo = ParentConversationInfo(), isViewingNote = false, viewNoteError = "", historyScrollGeneration = 0) }
        }
    }

    private fun loadView(parentNoteId: String, scrollToLatestOnSuccess: Boolean = false) {
        val normalizedId = parentNoteId.trim()
        if (normalizedId.isBlank()) return
        viewJob?.cancel()
        viewJob = scope.launch {
            _uiState.update { it.copy(viewedNote = null, viewedReplies = emptyList(), isViewingNote = true, viewNoteError = "") }
            val detail = ParentNotesRepository.loadDetail(normalizedId)
            val replies = ParentNotesRepository.loadReplies(normalizedId)
            if (detail.isFailure) {
                val error = detail.exceptionOrNull()
                scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel loadView failure parentNoteId=$normalizedId", error)
                _uiState.update { it.copy(isViewingNote = false, viewNoteError = error?.message.orEmpty().ifBlank { "Unable to load parent note." }) }
                return@launch
            }
            val note = detail.getOrThrow()
            val replyItems = replies.getOrElse { emptyList() }
            val contact = note.studentId?.trim()?.takeIf { it.isNotBlank() }?.let { studentId ->
                TeacherMyStudentsRepository.loadStudentDetail(studentId).getOrNull()?.let { student ->
                    val parent = student.parents.orEmpty().filter { it.isActive != false }
                        .firstOrNull { it.isPrimaryContact == true } ?: student.parents.orEmpty().firstOrNull { it.isActive != false }
                    ParentNoteStudentContact(
                        studentId = studentId,
                        parentId = parent?.parentId.orEmpty(),
                        rollNumber = student.rollNumber.orEmpty(),
                        parentName = parent?.name.orEmpty(),
                        mobile = parent?.mobile.orEmpty()
                    )
                }
            }
            val lastActivity = (listOfNotNull(note.updatedAt, note.createdAt) + replyItems.mapNotNull { it.createdAt })
                .maxOrNull().orEmpty()
            _uiState.update {
                it.copy(
                    viewedNote = note,
                    viewedReplies = replyItems,
                    viewedConversationInfo = ParentConversationInfo(
                        teacherName = note.createdByName.orEmpty().ifBlank { it.teacherName },
                        parentName = contact?.parentName.orEmpty(),
                        mobile = contact?.mobile.orEmpty(),
                        lastActivity = lastActivity,
                        conversationId = (note.parentNoteId ?: normalizedId).toConversationCode()
                    ),
                    isViewingNote = false,
                    viewNoteError = replies.exceptionOrNull()?.message.orEmpty(),
                    replyResetGeneration = if (scrollToLatestOnSuccess) it.replyResetGeneration + 1 else it.replyResetGeneration,
                    historyScrollGeneration = if (scrollToLatestOnSuccess) it.historyScrollGeneration + 1 else it.historyScrollGeneration
                )
            }
        }
    }

    private fun loadPickerOptions(
        classSectionId: String? = _uiState.value.filters.classSectionId,
        pageIndex: Int = _uiState.value.studentPickerPageIndex,
        reset: Boolean = pageIndex == 0
    ) {
        pickerJob?.cancel()
        pickerJob = scope.launch {
            val current = _uiState.value
            _uiState.update {
                it.copy(
                    pickerLoading = reset,
                    studentPickerLoadingMore = !reset,
                    pickerError = "",
                    studentPickerOptions = if (reset) emptyList() else it.studentPickerOptions
                )
            }
            val statistics = if (reset) TeacherMyStudentsRepository.loadDashboardStatistics() else Result.success(null)
            val students = TeacherMyStudentsRepository.loadPage(
                classSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() },
                query = current.studentPickerQuery,
                pageIndex = pageIndex,
                pageSize = STUDENT_PICKER_PAGE_SIZE,
                sort = ServerSort("name", ServerSortOrder.ASC)
            )
            if (statistics.isFailure || students.isFailure) {
                val error = statistics.exceptionOrNull() ?: students.exceptionOrNull()
                scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel loadPickerOptions failure", error)
                _uiState.update { it.copy(pickerLoading = false, pickerError = error?.message.orEmpty().ifBlank { "Unable to load teacher students." }) }
                return@launch
            }
            val response = students.getOrThrow()
            val rows = response.students.mapNotNull { item ->
                val id = (item.studentId ?: item.id).orEmpty().trim()
                val name = item.name.orEmpty().trim()
                val className = item.className.orEmpty().trim()
                val sectionName = item.sectionName.orEmpty().trim()
                if (id.isBlank() || name.isBlank() || className.isBlank() || sectionName.isBlank()) null
                else ParentNoteStudentOption(id, name, className, sectionName, item.rollNumber.orEmpty().trim())
            }.distinctBy { it.id }
            _uiState.update {
                it.copy(
                    pickerLoading = false,
                    studentPickerLoadingMore = false,
                    pickerError = "",
                    studentPickerPageIndex = pageIndex,
                    studentPickerHasMore = response.count?.let { total -> (pageIndex + 1) * STUDENT_PICKER_PAGE_SIZE < total }
                        ?: (rows.size == STUDENT_PICKER_PAGE_SIZE),
                    classPickerOptions = (statistics.getOrNull()?.assignedClasses ?: it.classSectionPickerOptions.map { option ->
                        TeacherClassStatistics(classSectionId = option.id, className = option.className, sectionName = option.sectionName)
                    }).orEmpty().mapNotNull { classSection ->
                        val className = classSection.className.orEmpty().trim()
                        if (className.isBlank()) null else ParentNoteClassOption(className, className)
                    }.distinctBy { it.name },
                    classSectionPickerOptions = (statistics.getOrNull()?.assignedClasses ?: it.classSectionPickerOptions.map { option ->
                        TeacherClassStatistics(classSectionId = option.id, className = option.className, sectionName = option.sectionName)
                    }).orEmpty().mapNotNull { classSection ->
                        val id = classSection.classSectionId.orEmpty().trim()
                        val className = classSection.className.orEmpty().trim()
                        val sectionName = classSection.sectionName.orEmpty().trim()
                        if (id.isBlank() || className.isBlank() || sectionName.isBlank()) null
                        else ParentNoteClassSectionOption(id, className, sectionName)
                    }.distinctBy { it.id },
                    sectionPickerOptions = (statistics.getOrNull()?.assignedClasses ?: it.classSectionPickerOptions.map { option ->
                        TeacherClassStatistics(classSectionId = option.id, className = option.className, sectionName = option.sectionName)
                    }).orEmpty().mapNotNull { classSection ->
                        val className = classSection.className.orEmpty().trim()
                        val sectionName = classSection.sectionName.orEmpty().trim()
                        if (className.isBlank() || sectionName.isBlank()) null
                        else ParentNoteSectionOption(className, sectionName)
                    }.distinct(),
                    studentPickerOptions = if (reset) rows else it.studentPickerOptions + rows
                )
            }
        }
    }

    private fun loadStudentContact(studentId: String) {
        val normalizedId = studentId.trim()
        if (normalizedId.isBlank()) return
        studentContactJob?.cancel()
        _uiState.update { it.copy(pickerLoading = true, pickerError = "", selectedStudentContact = null) }
        studentContactJob = scope.launch {
            val result = withTimeoutOrNull(STUDENT_CONTACT_TIMEOUT_MS) {
                TeacherMyStudentsRepository.loadStudentDetail(normalizedId)
            }
            if (result == null) {
                scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel loadStudentContact timeout studentId=$normalizedId")
                _uiState.update {
                    it.copy(pickerLoading = false, pickerError = "Parent details are taking too long to load. Select the student again to retry.")
                }
                return@launch
            }
            result.onSuccess { student ->
                val parent = student.parents.orEmpty().filter { it.isActive != false }
                    .firstOrNull { it.isPrimaryContact == true } ?: student.parents.orEmpty().firstOrNull { it.isActive != false }
                val parentId = parent?.parentId.orEmpty().trim()
                _uiState.update {
                    it.copy(
                        pickerLoading = false,
                        pickerError = "",
                        selectedStudentContact = ParentNoteStudentContact(normalizedId, parentId, student.rollNumber.orEmpty(), parent?.name.orEmpty(), parent?.mobile.orEmpty())
                    )
                }
            }.onFailure { throwable ->
                scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel loadStudentContact failure studentId=$normalizedId", throwable)
                _uiState.update { it.copy(pickerLoading = false, pickerError = throwable.message.orEmpty().ifBlank { "Unable to load this student's parent details." }) }
            }
        }
    }

    private fun createNote(draft: ParentNoteDraft) {
        createJob?.cancel()
        createJob = scope.launch {
            ParentNotesRepository.createNote(
                ParentNoteCreateRequest(
                    schoolId = schoolId,
                    studentId = draft.studentId,
                    parentId = draft.parentId,
                    title = draft.title,
                    message = draft.message,
                    attachmentFileIds = draft.attachmentFileIds
                ),
                currentUserId = UserSessionStore.getUserId()
            ).fold(
                onSuccess = {
                    scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel createNote success studentId=${draft.studentId}")
                    loadPage()
                    loadStatistics()
                },
                onFailure = { throwable -> scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel createNote failure", throwable) }
            )
        }
    }

    private fun updateNote(draft: ParentNoteEditDraft) {
        updateJob?.cancel()
        updateJob = scope.launch {
            ParentNotesRepository.updateNote(
                parentNoteId = draft.parentNoteId,
                request = ParentNoteUpdateRequest(
                    title = draft.title,
                    message = draft.message,
                    attachmentFileIds = draft.attachmentFileIds
                ),
                currentUserId = UserSessionStore.getUserId()
            ).fold(
                onSuccess = {
                    scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel updateNote success parentNoteId=${draft.parentNoteId}")
                    loadPage()
                    loadStatistics()
                },
                onFailure = { throwable -> scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel updateNote failure parentNoteId=${draft.parentNoteId}", throwable) }
            )
        }
    }

    private fun createReply(parentNoteId: String, message: String, attachmentFileIds: List<String>) {
        conversationJob?.cancel()
        conversationJob = scope.launch {
            ParentNotesRepository.createReply(parentNoteId, message, attachmentFileIds, UserSessionStore.getUserId()).fold(
                onSuccess = {
                    loadView(parentNoteId, scrollToLatestOnSuccess = true)
                    loadPage()
                    loadStatistics()
                },
                onFailure = { throwable -> scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel createReply failure parentNoteId=$parentNoteId", throwable) }
            )
        }
    }

    private fun deleteNote(parentNoteId: String) {
        conversationJob?.cancel()
        conversationJob = scope.launch {
            ParentNotesRepository.deleteNote(parentNoteId).fold(
                onSuccess = { loadPage(); loadStatistics() },
                onFailure = { throwable -> scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel deleteNote failure parentNoteId=$parentNoteId", throwable) }
            )
        }
    }

    private fun closeNote(parentNoteId: String) {
        conversationJob?.cancel()
        conversationJob = scope.launch {
            ParentNotesRepository.closeNote(parentNoteId, UserSessionStore.getUserId()).fold(
                onSuccess = {
                    loadView(parentNoteId)
                    loadPage()
                    loadStatistics()
                    _uiState.update { it.copy(conversationCloseGeneration = it.conversationCloseGeneration + 1) }
                },
                onFailure = { throwable -> scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel closeNote failure parentNoteId=$parentNoteId", throwable) }
            )
        }
    }

    private fun loadPage(debounce: Boolean = false) {
        listJob?.cancel()
        listJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            ParentNotesRepository.loadPage(
                search = state.searchQuery,
                status = state.filters.status.toApiStatus(),
                classSectionId = state.filters.classSectionId,
                studentId = state.filters.studentId,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc"
            ).fold(
                onSuccess = { result ->
                    val rows = result.items.map { it.toUi() }
                    _uiState.update { current ->
                        current.copy(
                            rows = rows,
                            totalCount = result.totalCount,
                            isLoading = false,
                            errorMessage = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel loadPage failure", throwable)
                    _uiState.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load parent notes." }) }
                }
            )
        }
    }

    private fun loadStatistics() {
        statisticsJob?.cancel()
        statisticsJob = scope.launch {
            _uiState.update { it.copy(isStatisticsLoading = true) }
            ParentNotesRepository.loadStatistics().fold(
                onSuccess = { response ->
                    val c = response.counters
                    _uiState.update { it.copy(totalNotesValue = c.counter("total", "total_notes") ?: 0, unreadValue = c.counter("unread") ?: 0, pendingParentValue = c.counter("pending_parent") ?: 0, pendingTeacherValue = c.counter("pending_teacher") ?: 0, teacherName = response.teacherName.orEmpty(), isStatisticsLoading = false) }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ParentNotesViewModel loadStatistics failure", throwable)
                    _uiState.update { it.copy(isStatisticsLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load parent note statistics." }) }
                }
            )
        }
    }

    fun clear() {
        listJob?.cancel()
        statisticsJob?.cancel()
        pickerJob?.cancel()
        studentContactJob?.cancel()
        createJob?.cancel()
        viewJob?.cancel()
        updateJob?.cancel()
        conversationJob?.cancel()
        scope.cancel()
    }
}

private fun Map<String, Int>.counter(vararg names: String): Int? = names.firstNotNullOfOrNull { name -> this[name] ?: this[name.lowercase()] ?: this[name.uppercase()] }

private fun String.toApiStatus(): String? = trim().takeIf { it.isNotBlank() }?.let {
    com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("PARENT_NOTE_STATUS", it)
}

private fun String.toConversationCode(): String =
    takeIf { !matches(Regex("[0-9a-fA-F]{8}-[0-9a-fA-F-]{27,}")) }?.ifBlank { "" } ?: ""

private fun ParentNotesSortField.toApiSortField(): String = when (this) {
    ParentNotesSortField.ID -> "parent_note_id"
    ParentNotesSortField.STUDENT -> "student_name"
    ParentNotesSortField.CLASS -> "class_name"
    ParentNotesSortField.TITLE -> "title"
    ParentNotesSortField.DATE -> "created_at"
    ParentNotesSortField.STATUS -> "status"
}

private fun ParentNoteResponse.toUi() = ParentNoteRowUi(
    backendId = id.orEmpty(),
    parentNoteId = parentNoteId.orEmpty().ifBlank { id.orEmpty() }.ifBlank { "—" },
    studentId = studentId.orEmpty(),
    studentName = studentName.orEmpty().ifBlank { "—" },
    className = className.orEmpty().ifBlank { "—" },
    section = sectionName.orEmpty().ifBlank { "—" },
    title = title.orEmpty().ifBlank { "Untitled note" },
    date = createdAt.toDisplayDate(),
    status = status.toDisplayStatus(),
    detail = message.orEmpty(),
    attachmentFileIds = attachmentFileIds.orEmpty(),
    teacherName = createdByName.orEmpty().ifBlank { "—" }
)

private fun String?.toDisplayStatus(): String {
    val value = this.orEmpty().trim()
    if (value.isBlank()) return "—"
    return com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.label("PARENT_NOTE_STATUS", value)
}

private fun String?.toDisplayDate(): String {
    val parts = this.orEmpty().substringBefore('T').split('-')
    if (parts.size != 3) return this.orEmpty().ifBlank { "—" }
    val month = parts[1].toIntOrNull() ?: return this.orEmpty()
    val label = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC").getOrNull(month - 1) ?: return this.orEmpty()
    return "${parts[2].padStart(2, '0')} $label ${parts[0]}"
}
